import {ApolloClient, gql, NormalizedCacheObject, useLazyQuery, useQuery} from '@apollo/client';
import {Query, VideoTranscript} from '../types/generated/graphql';

/**
 * Lightweight query for just the detected language facts — returns
 * without pulling the transcript body. Used by the always-visible
 * language chip on the video page header so we don't hydrate 30k+
 * chars of transcript just to show "lang: ar".
 */
const QUERY_GET_VIDEO_LANGUAGE = gql`
  query getVideoLanguage($videoId: String!) {
    getVideoLanguage(videoId: $videoId) {
      code
      confidence
      source
      secondary
    }
  }
`;

export const useGetVideoLanguage = (
  client: ApolloClient<NormalizedCacheObject>,
  videoId: string,
) => {
  type T = Pick<Query, 'getVideoLanguage'>;
  const {data, loading, error} = useQuery<T>(QUERY_GET_VIDEO_LANGUAGE, {
    client,
    variables: {videoId},
    fetchPolicy: 'cache-first',
    skip: !videoId,
  });
  return {data, loading, error};
};

const QUERY_GET_ADMIN_VIDEO_TRANSCRIPT = gql`
  query getAdminVideoTranscript($videoId: String!) {
    getAdminVideoTranscript(videoId: $videoId) {
      id
      duration
      transcript
      language {
        code
        confidence
        source
        secondary
      }
    }
  }
`;

/**
 * Lazy-loaded admin transcript fetch. Returns a `load(videoId)` trigger
 * + the query state (`data`, `loading`, `error`). The admin video page
 * keeps the transcript collapsed by default and calls `load()` only
 * when the panel is opened — transcripts can be 30k+ chars and there's
 * no point shipping them on every page view.
 */
export const useGetAdminVideoTranscript = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Query, 'getAdminVideoTranscript'>;
  const [load, result] = useLazyQuery<T>(QUERY_GET_ADMIN_VIDEO_TRANSCRIPT, {
    client,
    fetchPolicy: 'network-only',
  });
  return {load, ...result};
};

export type {VideoTranscript};
